import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { RestService } from '../rest/rest.service';
import { DatePipe } from '../../pipes/date.pipe';
import { OptionQuoteParams, QuoteResponse } from '../../domain/trading.domain';
import { SymbolType, SecuritySymbol } from '../../domain/symbol.domain';
import { MarketType } from '../../domain/market.domain';

/** Responsible for calling 'security.quotes' API.
 *  Has methods to prepare the query string parameters */
@Injectable({
  providedIn: 'root'
})
export class SecurityQuotesService {
  private readonly restService = inject(RestService);
  private readonly tdWbDate = inject(DatePipe);

  private readonly QUERY_SEPARATOR: string = '~';

  getQuotes(symbol: SecuritySymbol, optionQuoteParams?: OptionQuoteParams): Observable<Array<QuoteResponse>> {
    return this.restService.getResource('security.quotes')
      .get({ params: { symbols: [this.createQueryParameters(symbol, optionQuoteParams)] } });
  }

  private createQueryParameters(symbol: SecuritySymbol, optionQuoteParams?: OptionQuoteParams): string {
    if (optionQuoteParams !== undefined) {
      return this.createOptionSymbolQuery(symbol.market, optionQuoteParams.underlyingCompany) +
        this.createOptionParamsQuery(optionQuoteParams);
    }
    return this.createSymbolQuery(symbol);
  }

  private createOptionSymbolQuery(market: MarketType, underlyingCompany: string): string {
    // TODO work with backend to have something better than passing values like below:
    return `${SymbolType.OPTION}${this.QUERY_SEPARATOR}${underlyingCompany}${this.QUERY_SEPARATOR}${market}${this.QUERY_SEPARATOR}`;
  }

  private createOptionParamsQuery(optionQuoteParams: OptionQuoteParams): string {
    const strikePrice = optionQuoteParams.strikePrice;
    return `${optionQuoteParams.optionType}${this.QUERY_SEPARATOR}` +
      `${this.getOptionDate(optionQuoteParams.expiryDate)}${this.QUERY_SEPARATOR}${strikePrice}`;
  }

  private createSymbolQuery(symbol: SecuritySymbol): string {
    const symbolType = symbol.symbolType === SymbolType.OPTION_UNDERLYING || (symbol.symbolType === undefined) ?
      SymbolType.STOCK : symbol.symbolType;

    let market: string;
    if (symbol.marketType !== undefined) {
      market = symbol.market !== undefined ? symbol.market : symbol.marketType;
    } else {
      market = symbol.market !== undefined ? symbol.market : MarketType.CA;
    }

    return `${symbolType}~${symbol.symbol}~${market}`;
  }

  private getOptionDate(expiryDate: Date): string {
    return encodeURIComponent(this.tdWbDate.transform(expiryDate, { format: 'ISO_LOCAL_DATE_TIME' }));
  }
}

